/**
 * Safety Scan workflow page view model.
 *
 * Canonical authorities (never JSX constants):
 * - identity / window / place / control: SafetyScanProductPresentation (report + Case)
 * - review remaining: frozen Scan unreviewed (not live evidence-queue length)
 * - sign readiness: canSignSafetyScan / readiness
 * - signed freeze: persisted report status + post-finalize navigation
 * - history: existing Safety Scans / Records register
 *
 * Customer facts are passed in. This file contains no site, room, or condition strings.
 */

import { formatEpisodeCount } from '../copy/countNoun';
import { COUNT_NOT_AVAILABLE } from './optionalCount';
import { resolveScanVerifyPendingCount } from '../safetyScanReport/scanVerificationWork';
import {
  SIGN_SAFETY_SCAN_COPY,
  SIGN_SAFETY_SCAN_WORKFLOW_HELPER,
  type SafetyScanProductPresentation,
} from './safetyScanProductPresentation';

export const SAFETY_SCAN_WORKFLOW_STEPS = ['measure', 'review', 'sign', 'record'] as const;
export type SafetyScanWorkflowStepId = (typeof SAFETY_SCAN_WORKFLOW_STEPS)[number];

export const REVIEW_REQUIRED_EPISODES_LABEL = 'Review required episodes';
export const REVIEW_EVERY_IMAGE_FORBIDDEN = 'review every image';

export type SafetyScanWorkflowAuthority = {
  presentation: SafetyScanProductPresentation;
  signed: boolean;
  canSign: boolean;
  signing?: boolean;
  signedRecordHref?: string | null;
  historyHref?: string | null;
  downloadPdfLabel?: string | null;
  downloadZipLabel?: string | null;
  artifactPdfStatus?: string | null;
  artifactZipStatus?: string | null;
  previewReportId?: string | null;
  signedAtLabel?: string | null;
  comparisonReference?: {
    primaryLabel?: string | null;
    primaryIdentity?: string | null;
    primaryWindow?: string | null;
    secondaryLabel?: string | null;
    secondaryIdentity?: string | null;
    currentScanIdentity?: string | null;
    pairLabel?: string | null;
    controlInForceLabel?: string | null;
    initialBaselineEligible?: boolean;
    controlReferenceEligible?: boolean;
    preControlBaselineUnavailable?: boolean;
    historicalControlEffectiveness?: string | null;
    currentControlReferenceIdentity?: string | null;
    initialBaselineIdentity?: string | null;
    historyHref?: string | null;
  } | null;
};

export type SafetyScanWorkflowViewModel = {
  step: SafetyScanWorkflowStepId;
  strip: Array<{
    id: SafetyScanWorkflowStepId;
    number: 1 | 2 | 3 | 4;
    label: string;
    current: boolean;
    complete: boolean;
  }>;
  currentScan: {
    title: string;
    conditionLabel: string;
    locationLine: string;
    windowLabel: string;
    caseLabel: string;
    stateLabel: string;
    controlRecordedLabel: string;
    siteLabel: string;
    cameraLabel: string;
  };
  review: {
    required: boolean;
    headline: string;
    detail: string;
    ctaLabel: string;
    pendingCount: number | null;
    reviewedLabel: string;
    candidateLabel: string;
  };
  sign: {
    ready: boolean;
    headline: string;
    helper: string;
    ctaLabel: string;
    disabledReason: string;
    previewAvailable: boolean;
    previewCtaLabel: string;
    previewReportId: string;
  };
  signedRecord: {
    signed: boolean;
    visible: boolean;
    headline: string;
    detail: string;
    signedAtLabel: string;
    href: string | null;
    ctaLabel: string;
    viewCtaLabel: string;
    downloadCtaLabel: string;
  };
  history: {
    label: string;
    href: string;
  };
  comparisonReference: {
    primaryLabel: string;
    primaryIdentity: string;
    primaryWindow: string;
    secondaryLine: string;
    pairLabel: string;
    controlInForceLabel: string;
    currentScanIdentity: string;
    initialBaselineEligible: boolean;
    controlReferenceEligible: boolean;
    preControlBaselineUnavailable: boolean;
    historicalControlEffectiveness: string;
    currentControlReferenceIdentity: string;
    initialBaselineIdentity: string;
  };
  downloads: {
    primary: boolean;
    pdfLabel: string;
    zipLabel: string;
    pdfStatus: string;
    zipStatus: string;
    helper: string;
  };
};

function firstLabel(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text) return text;
  }
  return '';
}

export function formatPendingEpisodeReviewLabel(count: number): string {
  const n = Math.max(0, Math.trunc(Number(count) || 0));
  const verb = n === 1 ? 'requires' : 'require';
  return `${formatEpisodeCount(n)} ${verb} review before this Safety Scan can be signed`;
}

export function formatReviewedEpisodeLabel(count: number | null): string {
  if (count == null) return `Episodes reviewed · ${COUNT_NOT_AVAILABLE}`;
  return `${formatEpisodeCount(count)} reviewed`;
}

export function resolveSafetyScanWorkflowStep(args: {
  signed: boolean;
  reviewRequired: boolean;
  readyToSign?: boolean;
}): SafetyScanWorkflowStepId {
  if (args.signed) return 'record';
  if (args.reviewRequired) return 'review';
  if (args.readyToSign) return 'sign';
  return 'measure';
}

export function buildSafetyScanWorkflowViewModel(
  args: SafetyScanWorkflowAuthority,
): SafetyScanWorkflowViewModel {
  const problem = args.presentation.problem;
  const evidence = args.presentation.evidence;
  const readiness = args.presentation.readiness;
  const pending = resolveScanVerifyPendingCount({
    frozenUnreviewed: evidence.pendingVerifyCount,
  });
  const reviewRequired = !args.signed && (
    (pending != null && pending > 0)
    || (pending == null && Boolean(evidence.verifyRequired))
  );
  const step = resolveSafetyScanWorkflowStep({
    signed: Boolean(args.signed),
    reviewRequired,
    readyToSign: Boolean(args.canSign) && !reviewRequired && !args.signed,
  });
  const reviewed = evidence.funnel.reviewed;
  const candidate = evidence.funnel.candidate;
  const historyHref = String(args.historyHref || '').trim() || '/acceptance/reports';
  const signedHref = String(args.signedRecordHref || '').trim() || null;
  const readyToSign = Boolean(args.canSign) && !reviewRequired && !args.signed;

  return {
    step,
    strip: [
      {
        id: 'measure',
        number: 1,
        label: 'MEASURE',
        current: step === 'measure',
        complete: step !== 'measure',
      },
      {
        id: 'review',
        number: 2,
        label: 'REVIEW',
        current: step === 'review',
        complete: !reviewRequired,
      },
      {
        id: 'sign',
        number: 3,
        label: 'SIGN',
        current: step === 'sign',
        complete: Boolean(args.signed),
      },
      {
        id: 'record',
        number: 4,
        label: 'RECORD',
        current: step === 'record',
        complete: Boolean(args.signed),
      },
    ],
    currentScan: {
      title: firstLabel(problem.conditionLabel, 'Current Safety Scan'),
      conditionLabel: problem.conditionLabel,
      locationLine: problem.locationLine,
      windowLabel: problem.windowLabel,
      caseLabel: problem.caseLabel,
      stateLabel: args.signed
        ? 'Signed Safety Scan'
        : (reviewRequired
          ? 'Review required'
          : (readyToSign ? 'Ready to sign' : (step === 'measure' ? 'Measurement in progress' : problem.stateLabel))),
      controlRecordedLabel: readiness.controlRecordedLabel,
      siteLabel: problem.siteLabel,
      cameraLabel: problem.cameraLabel,
    },
    review: {
      required: reviewRequired,
      headline: reviewRequired
        ? (pending != null ? formatPendingEpisodeReviewLabel(pending) : 'Required episodes still need review')
        : (pending === 0 ? 'Review complete' : readiness.reviewCompleteLabel),
      detail: reviewRequired
        ? 'Review the required episodes in this scan window, then return to sign. This is not a review of every image.'
        : 'Required episodes in this scan window have a review disposition.',
      ctaLabel: pending != null && pending > 0
        ? `${REVIEW_REQUIRED_EPISODES_LABEL} — ${pending} pending`
        : REVIEW_REQUIRED_EPISODES_LABEL,
      pendingCount: pending,
      reviewedLabel: formatReviewedEpisodeLabel(reviewed),
      candidateLabel: candidate == null ? '' : `${formatEpisodeCount(candidate)} in this Scan window`,
    },
    sign: {
      ready: readyToSign,
      headline: args.signed
        ? 'This Safety Scan is signed'
        : (readyToSign ? 'Ready to sign' : readiness.headline),
      helper: SIGN_SAFETY_SCAN_WORKFLOW_HELPER,
      ctaLabel: args.signing ? 'Signing Safety Scan...' : SIGN_SAFETY_SCAN_COPY.button,
      disabledReason: args.signed
        ? 'This Safety Scan is already signed.'
        : (reviewRequired
          ? 'Sign is available after required episodes are reviewed.'
          : (readyToSign ? '' : readiness.headline)),
      previewAvailable: readyToSign && Boolean(String(args.previewReportId || '').trim()),
      previewCtaLabel: 'Preview record',
      previewReportId: readyToSign ? String(args.previewReportId || '').trim() : '',
    },
    signedRecord: {
      signed: Boolean(args.signed),
      visible: Boolean(args.signed),
      headline: 'Signed Record',
      detail: args.signed
        ? (signedHref
          ? 'This Safety Scan is frozen as a Signed Record. New activity is measured in a later Safety Scan.'
          : 'This Safety Scan is signed. The exact Signed Record revision is not available yet. Open the signed artifact.')
        : '',
      signedAtLabel: args.signed ? String(args.signedAtLabel || '').trim() : '',
      href: args.signed ? signedHref : null,
      ctaLabel: 'VIEW SIGNED RECORD',
      viewCtaLabel: 'VIEW SIGNED RECORD',
      downloadCtaLabel: 'DOWNLOAD SIGNED RECORD',
    },
    history: {
      label: 'View Safety Scan history',
      href: historyHref,
    },
    comparisonReference: {
      primaryLabel: firstLabel(args.comparisonReference?.primaryLabel, 'Initial baseline'),
      primaryIdentity: firstLabel(args.comparisonReference?.primaryIdentity, 'Not established'),
      primaryWindow: firstLabel(args.comparisonReference?.primaryWindow, 'Not established'),
      secondaryLine: [
        args.comparisonReference?.secondaryLabel,
        args.comparisonReference?.secondaryIdentity,
      ].filter(Boolean).join(' · '),
      pairLabel: firstLabel(args.comparisonReference?.pairLabel, 'Not established vs Current Scan'),
      controlInForceLabel: firstLabel(args.comparisonReference?.controlInForceLabel, 'Not established'),
      currentScanIdentity: firstLabel(args.comparisonReference?.currentScanIdentity, ''),
      initialBaselineEligible: Boolean(args.comparisonReference?.initialBaselineEligible)
        && args.comparisonReference?.preControlBaselineUnavailable !== true,
      controlReferenceEligible: Boolean(args.comparisonReference?.controlReferenceEligible),
      preControlBaselineUnavailable: Boolean(args.comparisonReference?.preControlBaselineUnavailable),
      historicalControlEffectiveness: firstLabel(
        args.comparisonReference?.historicalControlEffectiveness,
        '',
      ),
      currentControlReferenceIdentity: firstLabel(
        args.comparisonReference?.currentControlReferenceIdentity,
        'Not established',
      ),
      initialBaselineIdentity: firstLabel(
        args.comparisonReference?.initialBaselineIdentity,
        args.comparisonReference?.preControlBaselineUnavailable ? 'Unavailable' : 'Not established',
      ),
    },
    downloads: {
      primary: false,
      pdfLabel: String(args.downloadPdfLabel || '').trim() || 'Download Customer Safety Scan PDF',
      zipLabel: String(args.downloadZipLabel || '').trim() || 'Download Scan Package',
      pdfStatus: String(args.artifactPdfStatus || '').trim() || 'NOT_GENERATED',
      zipStatus: String(args.artifactZipStatus || '').trim() || 'NOT_GENERATED',
      helper: 'Downloads are optional. Downloading does not sign. Opening this page does not generate a package or PDF.',
    },
  };
}
